import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Download, Users, Receipt, CalendarDays, Award, TrendingUp, IndianRupee, UserCheck, UserX, Clock, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';

const CLASSES = [
  { value: '1', label: 'Class 6' }, { value: '2', label: 'Class 7' },
  { value: '3', label: 'Class 8' }, { value: '4', label: 'Class 9' },
  { value: '5', label: 'Class 10' }, { value: '6', label: 'Class 11' },
  { value: '7', label: 'Class 12' },
];

const COLORS = ['#6366f1', '#f43f5e', '#22c55e', '#f59e0b', '#06b6d4', '#ec4899', '#8b5cf6'];

const reportTypes = [
  { id: 'students', label: 'Students by Class', icon: Users, color: 'bg-purple-500' },
  { id: 'fees', label: 'Fee Collection', icon: IndianRupee, color: 'bg-amber-500' },
  { id: 'attendance', label: 'Attendance', icon: CalendarDays, color: 'bg-lime-500' },
  { id: 'scholarships', label: 'Scholarships', icon: Award, color: 'bg-rose-500' },
];

export default function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState('students');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [showChart, setShowChart] = useState(true);

  // Summary stats
  const { data: summary } = useQuery({
    queryKey: ['report-summary', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      return (await api.get<any>(`/api/reports/summary?${params.toString()}`)).data;
    },
  });

  // Students report
  const { data: studentData, isLoading: studentLoading } = useQuery({
    queryKey: ['report-students', selectedClass, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedClass) params.append('classId', selectedClass);
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      return (await api.get<any>(`/api/reports/students?${params.toString()}`)).data || [];
    },
    enabled: selectedReport === 'students',
  });

  // Fees report
  const { data: feeData, isLoading: feeLoading } = useQuery({
    queryKey: ['report-fees', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      return (await api.get<any>(`/api/reports/fees?${params.toString()}`)).data || [];
    },
    enabled: selectedReport === 'fees',
  });

  // Attendance report
  const { data: attendanceData, isLoading: attendanceLoading } = useQuery({
    queryKey: ['report-attendance', dateFrom, dateTo, selectedClass],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      if (selectedClass) params.append('classId', selectedClass);
      return (await api.get<any>(`/api/reports/attendance?${params.toString()}`)).data || [];
    },
    enabled: selectedReport === 'attendance',
  });

  // Scholarships report
  const { data: scholarshipData, isLoading: scholarshipLoading } = useQuery({
    queryKey: ['report-scholarships'],
    queryFn: async () => {
      return (await api.get<any>('/api/reports/scholarships')).data || [];
    },
    enabled: selectedReport === 'scholarships',
  });

  const currentData =
    selectedReport === 'students' ? studentData :
    selectedReport === 'fees' ? feeData :
    selectedReport === 'attendance' ? attendanceData :
    selectedReport === 'scholarships' ? scholarshipData : [];

  const currentLoading =
    selectedReport === 'students' ? studentLoading :
    selectedReport === 'fees' ? feeLoading :
    selectedReport === 'attendance' ? attendanceLoading :
    selectedReport === 'scholarships' ? scholarshipLoading : false;

  const handleExport = () => {
    const rows = Array.isArray(currentData) ? currentData : [currentData].filter(Boolean);
    if (!rows.length) { toast.error('No data to export'); return; }
    const csvContent = [
      Object.keys(rows[0]).join(','),
      ...rows.map((row: Record<string, unknown>) => Object.values(row).map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport}-report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  const handleExportPDF = () => { window.print(); };

  // Chart data for students — by status
  const studentStatusChart = [
    { name: 'Active', value: summary?.activeStudents || 0, color: '#22c55e' },
    { name: 'Drop', value: summary?.dropStudents || 0, color: '#f43f5e' },
    { name: 'Enquiry', value: summary?.enquiryStudents || 0, color: '#f59e0b' },
  ].filter(d => d.value > 0);

  // Attendance chart data
  const attendanceChart = (attendanceData || []).slice(0, 30).map((d: any) => ({
    date: d.attendance_date?.slice(5) || d.date,
    Present: d.present || 0,
    Absent: d.absent || 0,
    Late: d.late || 0,
  }));

  // Fee totals
  const feeTotal = (feeData || []).reduce((sum: number, f: any) => sum + (f.total_amount || 0), 0);
  const feeCollected = (feeData || []).reduce((sum: number, f: any) => sum + (f.paid_amount || 0), 0);
  const feePending = feeTotal - feeCollected;

  return (
    <div className="space-y-4 sm:space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
            Reports
          </h1>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">Analytics, charts, and exports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-9 gap-1.5 text-xs" onClick={handleExportPDF}>
            <Download className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button size="sm" className="h-9 gap-1.5 text-xs" onClick={handleExport}>
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <div className="rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20 p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <p className="text-[10px] sm:text-xs text-purple-600 dark:text-purple-300 font-medium">Total Students</p>
            </div>
            <p className="text-xl sm:text-2xl font-black text-light-text dark:text-dark-text">{summary.totalStudents}</p>
          </div>
          <div className="rounded-xl bg-lime-50 dark:bg-lime-500/10 border border-lime-100 dark:border-lime-500/20 p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-lime-500 flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-white" />
              </div>
              <p className="text-[10px] sm:text-xs text-lime-600 dark:text-lime-300 font-medium">Active</p>
            </div>
            <p className="text-xl sm:text-2xl font-black text-light-text dark:text-dark-text">{summary.activeStudents}</p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                <IndianRupee className="w-4 h-4 text-white" />
              </div>
              <p className="text-[10px] sm:text-xs text-amber-600 dark:text-amber-300 font-medium">Collected</p>
            </div>
            <p className="text-xl sm:text-2xl font-black text-light-text dark:text-dark-text">₹{Number(summary.collected || 0).toLocaleString('en-IN')}</p>
          </div>
          <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-rose-500 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <p className="text-[10px] sm:text-xs text-rose-600 dark:text-rose-300 font-medium">Pending</p>
            </div>
            <p className="text-xl sm:text-2xl font-black text-light-text dark:text-dark-text">₹{Number(summary.pending || 0).toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      {/* Report Type Selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {reportTypes.map((report) => (
          <button
            key={report.id}
            onClick={() => { setSelectedReport(report.id); setSelectedClass(''); }}
            className={`rounded-xl p-3 sm:p-4 text-left transition-all border ${
              selectedReport === report.id
                ? 'ring-2 ring-purple-500 border-purple-200 dark:border-purple-500/30 bg-purple-50/50 dark:bg-purple-500/5'
                : 'border-light-border dark:border-dark-border bg-white dark:bg-dark-surface hover:bg-light-elevated/50 dark:hover:bg-dark-elevated/50'
            }`}
          >
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${report.color} flex items-center justify-center mb-2`}>
              <report.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <p className="text-xs sm:text-sm font-semibold text-light-text dark:text-dark-text">{report.label}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border p-3 sm:p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <button
            onClick={() => setShowChart(true)}
            className={`text-[10px] sm:text-xs px-3 py-1 rounded-full font-medium transition-colors ${showChart ? 'bg-purple-500 text-white' : 'bg-light-elevated dark:bg-dark-elevated text-light-text-muted'}`}
          >
            Chart
          </button>
          <button
            onClick={() => setShowChart(false)}
            className={`text-[10px] sm:text-xs px-3 py-1 rounded-full font-medium transition-colors ${!showChart ? 'bg-purple-500 text-white' : 'bg-light-elevated dark:bg-dark-elevated text-light-text-muted'}`}
          >
            Table
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-end">
          <div className="flex-1 min-w-0">
            <Label className="text-[10px] text-light-text-muted mb-0.5 block">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-xs" />
          </div>
          <div className="flex-1 min-w-0">
            <Label className="text-[10px] text-light-text-muted mb-0.5 block">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-xs" />
          </div>
          {(selectedReport === 'students' || selectedReport === 'attendance') && (
            <div className="flex-1 min-w-0">
              <Label className="text-[10px] text-light-text-muted mb-0.5 block">Class</Label>
              <Select value={selectedClass || 'all'} onValueChange={(v) => setSelectedClass(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="All classes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {CLASSES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button variant="outline" size="sm" className="h-9 text-xs shrink-0" onClick={() => { setDateFrom(''); setDateTo(''); setSelectedClass(''); }}>
            Clear
          </Button>
        </div>
      </div>

      {/* Charts & Data */}
      {currentLoading ? (
        <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto" />
        </div>
      ) : (
        <>
          {/* STUDENTS REPORT */}
          {selectedReport === 'students' && (
            <div className="space-y-4">
              {showChart && summary && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  <Card>
                    <CardHeader className="pb-1"><CardTitle className="text-sm">Student Status</CardTitle></CardHeader>
                    <CardContent>
                      {studentStatusChart.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={studentStatusChart} cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={4} dataKey="value">
                              {studentStatusChart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-8 text-light-text-muted text-xs">No data</div>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-1"><CardTitle className="text-sm">Students by Class</CardTitle></CardHeader>
                    <CardContent>
                      {studentData?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={CLASSES.map(cls => ({
                            name: cls.label,
                            students: (studentData || []).filter((s: any) => String(s.class_id) === cls.value).length
                          })).filter(d => d.students > 0)}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Bar dataKey="students" fill="#6366f1" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-8 text-light-text-muted text-xs">No data</div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
              {!showChart && studentData?.length > 0 && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-light-border dark:border-dark-border bg-light-elevated/50 dark:bg-dark-elevated/50">
                          {['Name', 'Gender', 'DOB', 'Roll', 'Class', 'Batch', 'Status', 'Admission', 'School'].map(h => (
                            <th key={h} className="text-left text-[10px] font-semibold text-light-text-muted px-3 py-2.5 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {studentData.map((s: any, i: number) => (
                          <tr key={i} className="border-b border-light-border/40 dark:border-dark-border/40 hover:bg-light-elevated/30 dark:hover:bg-dark-elevated/30">
                            <td className="px-3 py-2 text-xs font-medium">{s.student_name}</td>
                            <td className="px-3 py-2 text-[10px] capitalize">{s.gender}</td>
                            <td className="px-3 py-2 text-[10px]">{s.date_of_birth || '-'}</td>
                            <td className="px-3 py-2 text-[10px]">{s.roll_no || '-'}</td>
                            <td className="px-3 py-2 text-[10px]">{s.class_name || '-'}</td>
                            <td className="px-3 py-2 text-[10px]">{s.batch_name || '-'}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                s.status === 'active' ? 'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300' :
                                s.status === 'drop' || s.status === 'left' ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300' :
                                'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                              }`}>{s.status}</span>
                            </td>
                            <td className="px-3 py-2 text-[10px]">{s.admission_date || '-'}</td>
                            <td className="px-3 py-2 text-[10px]">{s.school_name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!showChart && !studentData?.length && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border p-10 text-center">
                  <Users className="w-10 h-10 text-light-text-muted mx-auto mb-3" />
                  <p className="text-sm text-light-text-muted">No students found for these filters</p>
                </div>
              )}
            </div>
          )}

          {/* FEES REPORT */}
          {selectedReport === 'fees' && (
            <div className="space-y-4">
              {showChart && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  <Card>
                    <CardHeader className="pb-1"><CardTitle className="text-sm">Fee Overview</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="text-center">
                          <p className="text-[10px] text-light-text-muted">Total</p>
                          <p className="text-lg font-black">₹{(feeTotal).toLocaleString('en-IN')}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-lime-600">Collected</p>
                          <p className="text-lg font-black text-lime-600">₹{feeCollected.toLocaleString('en-IN')}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-rose-600">Pending</p>
                          <p className="text-lg font-black text-rose-600">₹{feePending.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                      {feeTotal > 0 && (
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={[
                              { name: 'Collected', value: feeCollected, color: '#22c55e' },
                              { name: 'Pending', value: feePending, color: '#f43f5e' },
                            ]} cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={3} dataKey="value">
                              <Cell fill="#22c55e" />
                              <Cell fill="#f43f5e" />
                            </Pie>
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-1"><CardTitle className="text-sm">Invoice Status</CardTitle></CardHeader>
                    <CardContent>
                      {feeData?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={[
                            { status: 'Paid', count: feeData.filter((f: any) => f.status === 'paid').length },
                            { status: 'Unpaid', count: feeData.filter((f: any) => f.status === 'unpaid').length },
                            { status: 'Partial', count: feeData.filter((f: any) => f.status === 'partial').length },
                            { status: 'Cancelled', count: feeData.filter((f: any) => f.status === 'cancelled').length },
                          ].filter(d => d.count > 0)}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-8 text-light-text-muted text-xs">No data</div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
              {!showChart && feeData?.length > 0 && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-light-border dark:border-dark-border bg-light-elevated/50 dark:bg-dark-elevated/50">
                          {['Invoice #', 'Student', 'Total', 'Paid', 'Due', 'Status', 'Due Date'].map(h => (
                            <th key={h} className="text-left text-[10px] font-semibold text-light-text-muted px-3 py-2.5 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {feeData.map((f: any, i: number) => (
                          <tr key={i} className="border-b border-light-border/40 dark:border-dark-border/40 hover:bg-light-elevated/30 dark:hover:bg-dark-elevated/30">
                            <td className="px-3 py-2 text-xs font-mono font-medium">{f.invoice_no}</td>
                            <td className="px-3 py-2 text-xs">{f.student_name || '-'}</td>
                            <td className="px-3 py-2 text-[10px]">₹{f.total_amount}</td>
                            <td className="px-3 py-2 text-[10px] text-lime-600">₹{f.paid_amount || 0}</td>
                            <td className="px-3 py-2 text-[10px] text-rose-600">₹{f.due_amount || 0}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                f.status === 'paid' ? 'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300' :
                                f.status === 'partial' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' :
                                f.status === 'cancelled' ? 'bg-gray-100 text-gray-500 dark:bg-gray-500/20 dark:text-gray-400' :
                                'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300'
                              }`}>{f.status}</span>
                            </td>
                            <td className="px-3 py-2 text-[10px]">{f.due_date || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!showChart && !feeData?.length && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border p-10 text-center">
                  <IndianRupee className="w-10 h-10 text-light-text-muted mx-auto mb-3" />
                  <p className="text-sm text-light-text-muted">No fee data for these filters</p>
                </div>
              )}
            </div>
          )}

          {/* ATTENDANCE REPORT */}
          {selectedReport === 'attendance' && (
            <div className="space-y-4">
              {showChart && attendanceData?.length > 0 && (
                <Card>
                  <CardHeader className="pb-1"><CardTitle className="text-sm">Daily Attendance</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={attendanceChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="Present" stroke="#22c55e" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="Absent" stroke="#f43f5e" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="Late" stroke="#f59e0b" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
              {showChart && !attendanceData?.length && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border p-10 text-center">
                  <CalendarDays className="w-10 h-10 text-light-text-muted mx-auto mb-3" />
                  <p className="text-sm text-light-text-muted">No attendance data for these filters</p>
                </div>
              )}
              {!showChart && attendanceData?.length > 0 && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-light-border dark:border-dark-border bg-light-elevated/50 dark:bg-dark-elevated/50">
                          {['Date', 'Total', 'Present', 'Absent', 'Late', 'Leave', '% Present'].map(h => (
                            <th key={h} className="text-left text-[10px] font-semibold text-light-text-muted px-3 py-2.5 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceData.map((d: any, i: number) => {
                          const pct = d.total > 0 ? Math.round((d.present / d.total) * 100) : 0;
                          return (
                            <tr key={i} className="border-b border-light-border/40 dark:border-dark-border/40 hover:bg-light-elevated/30 dark:hover:bg-dark-elevated/30">
                              <td className="px-3 py-2 text-xs font-medium">{d.attendance_date}</td>
                              <td className="px-3 py-2 text-[10px] font-semibold">{d.total}</td>
                              <td className="px-3 py-2 text-[10px] text-lime-600 font-medium">{d.present}</td>
                              <td className="px-3 py-2 text-[10px] text-rose-600 font-medium">{d.absent}</td>
                              <td className="px-3 py-2 text-[10px] text-amber-600">{d.late}</td>
                              <td className="px-3 py-2 text-[10px] text-sky-600">{d.leave}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-12 h-1.5 bg-light-elevated dark:bg-dark-elevated rounded-full overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#f43f5e' }} />
                                  </div>
                                  <span className="text-[10px] font-medium">{pct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!showChart && !attendanceData?.length && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border p-10 text-center">
                  <CalendarDays className="w-10 h-10 text-light-text-muted mx-auto mb-3" />
                  <p className="text-sm text-light-text-muted">No attendance data for these filters</p>
                </div>
              )}
            </div>
          )}

          {/* SCHOLARSHIPS REPORT */}
          {selectedReport === 'scholarships' && (
            <div className="space-y-4">
              {showChart && scholarshipData?.length > 0 && (
                <Card>
                  <CardHeader className="pb-1"><CardTitle className="text-sm">Scholarship Distribution</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={(() => {
                        const map: Record<string, number> = {};
                        scholarshipData.forEach((s: any) => {
                          const name = s.scheme_name || 'Unknown';
                          map[name] = (map[name] || 0) + 1;
                        });
                        return Object.entries(map).map(([name, count]) => ({ name, count }));
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#ec4899" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
              {!showChart && scholarshipData?.length > 0 && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-light-border dark:border-dark-border bg-light-elevated/50 dark:bg-dark-elevated/50">
                          {['Student', 'Scheme', 'Amount', 'Academic Year', 'Status'].map(h => (
                            <th key={h} className="text-left text-[10px] font-semibold text-light-text-muted px-3 py-2.5 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {scholarshipData.map((sch: any, i: number) => (
                          <tr key={i} className="border-b border-light-border/40 dark:border-dark-border/40 hover:bg-light-elevated/30 dark:hover:bg-dark-elevated/30">
                            <td className="px-3 py-2 text-xs font-medium">{sch.student_name}</td>
                            <td className="px-3 py-2 text-[10px]">{sch.scheme_name || '-'}</td>
                            <td className="px-3 py-2 text-[10px] font-medium">₹{sch.amount || 0}</td>
                            <td className="px-3 py-2 text-[10px]">{sch.academic_year || '-'}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                sch.status === 'approved' ? 'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300' :
                                sch.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' :
                                'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300'
                              }`}>{sch.status || '-'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!showChart && !scholarshipData?.length && (
                <div className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border p-10 text-center">
                  <Award className="w-10 h-10 text-light-text-muted mx-auto mb-3" />
                  <p className="text-sm text-light-text-muted">No scholarship data found</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
